import { config } from "./config";

const DEDUPE_WINDOW_MS = 60 * 1000;
const WINDOW_ERROR_DELAY_MS = 125;
const MAX_PAYLOAD_BYTES = 20 * 1024;
const recentReports = new Map();

let getAppState;
let listenersInstalled = false;

const truncate = (value, maxLength) => {
	if (typeof value !== "string") return undefined;
	return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
};

const normalizeMessage = (message) =>
	String(message || "Unknown client error")
		.replace(/^(?:uncaught\s+)?(?:\(in promise\)\s*)?(?:error:\s*)?/i, "")
		.trim() || "Unknown client error";

const errorDetails = (value) => {
	if (value instanceof Error) {
		return { message: value.message, stack: value.stack };
	}
	if (typeof value === "string") {
		return { message: value };
	}
	try {
		return { message: JSON.stringify(value) };
	} catch {
		return { message: String(value) };
	}
};

const buildDebugContext = () => {
	try {
		const state = getAppState?.();
		const authUser = state?.auth?.currentAuthenticatedUser;
		return {
			auth: authUser
				? {
						id: authUser.id || authUser.username || authUser.attributes?.sub,
						email: authUser.email || authUser.attributes?.email,
					}
				: null,
			route: `${window.location.pathname}${window.location.search}${window.location.hash}`,
		};
	} catch (error) {
		console.error("Could not build client error debug context", error);
		return { route: window.location.pathname };
	}
};

const jsonSize = (value) => new Blob([JSON.stringify(value)]).size;

const fitPayload = (payload) => {
	const fitted = { ...payload };
	if (jsonSize(fitted) <= MAX_PAYLOAD_BYTES) return fitted;

	fitted.debugContext = { route: payload.debugContext?.route };
	if (jsonSize(fitted) <= MAX_PAYLOAD_BYTES) return fitted;

	fitted.componentStack = truncate(fitted.componentStack, 4000);
	fitted.stack = truncate(fitted.stack, 8000);
	fitted.message = truncate(fitted.message, 2000);

	while (jsonSize(fitted) > MAX_PAYLOAD_BYTES && (fitted.stack || fitted.componentStack)) {
		if ((fitted.stack?.length || 0) >= (fitted.componentStack?.length || 0)) {
			fitted.stack = truncate(fitted.stack, Math.floor(fitted.stack.length / 2));
		} else {
			fitted.componentStack = truncate(fitted.componentStack, Math.floor(fitted.componentStack.length / 2));
		}
	}
	return fitted;
};

export const reportClientError = ({ type, message, stack, componentStack, source }) => {
	try {
		const normalizedMessage = normalizeMessage(message);
		const now = Date.now();
		const previousReport = recentReports.get(normalizedMessage);
		if (previousReport && now - previousReport < DEDUPE_WINDOW_MS) return;

		recentReports.set(normalizedMessage, now);
		for (const [fingerprint, reportedAt] of recentReports) {
			if (now - reportedAt >= DEDUPE_WINDOW_MS) recentReports.delete(fingerprint);
		}

		const payload = fitPayload({
			type,
			message: truncate(normalizedMessage, 4000),
			stack: truncate(stack, 12000),
			componentStack: truncate(componentStack, 10000),
			source: truncate(source, 1000),
			url: truncate(window.location.href, 2000),
			userAgent: truncate(window.navigator.userAgent, 1000),
			environment: config.environment,
			reportedAt: new Date().toISOString(),
			debugContext: buildDebugContext(),
		});

		fetch(`${config.apiUrl}/client-errors`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(payload),
			keepalive: true,
		}).catch((error) => console.error("Could not report client error", error));
	} catch (error) {
		console.error("Could not prepare client error report", error);
	}
};

export const installGlobalErrorHandlers = (store) => {
	getAppState = store?.getState ? () => store.getState() : undefined;
	if (listenersInstalled) return;
	listenersInstalled = true;

	window.addEventListener("error", (event) => {
		if (!event.error && !event.message) return;
		const details = errorDetails(event.error || event.message);
		window.setTimeout(() => {
			reportClientError({
				type: "window",
				message: details.message,
				stack: details.stack,
				source: [event.filename, event.lineno, event.colno].filter(Boolean).join(":") || "window",
			});
		}, WINDOW_ERROR_DELAY_MS);
	});

	window.addEventListener("unhandledrejection", (event) => {
		const details = errorDetails(event.reason);
		reportClientError({
			type: "unhandledrejection",
			message: details.message,
			stack: details.stack,
			source: "unhandledrejection",
		});
	});
};
